import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  UserCheck, FileText, Users,
  ArrowRight, ArrowLeft, CheckCircle2, Sparkles,
} from 'lucide-react';

const ONBOARDING_KEY = 'onboarding_completed';

export function useOnboarding() {
  const isComplete = localStorage.getItem(ONBOARDING_KEY) === 'true';
  const completeOnboarding = () => localStorage.setItem(ONBOARDING_KEY, 'true');
  return { isOnboardingComplete: isComplete, completeOnboarding };
}

interface StepProps {
  title: string;
  description: string;
  icon: React.ElementType;
  content: string;
}

const steps: StepProps[] = [
  {
    title: 'Welcome',
    description: 'Your hub for managing automation advisory engagements',
    icon: Sparkles,
    content: 'This is where you run your advisory practice: the profile prospective clients find you by, the companies you advise, and a map of how each of them works. Four short steps.',
  },
  {
    title: 'Set up your public profile',
    description: 'Tell prospective clients who you are',
    icon: UserCheck,
    content: 'Your bio, industry expertise and portfolio live on the Public profile page. Publish it, and once BitSwan has certified you, you appear in the advisor catalogue. You can advise clients you bring yourself before then.',
  },
  {
    title: 'Add your first client',
    description: 'Start building your client portfolio',
    icon: Users,
    content: 'The Clients page holds the companies you advise, with their details and contacts. Everything else you record belongs to a client.',
  },
];

export default function OnboardingWizard({ onComplete }: { onComplete: () => void }) {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const isLastStep = currentStep === steps.length - 1;
  const step = steps[currentStep];
  const StepIcon = step.icon;

  const handleFinish = () => {
    onComplete();
  };

  const handleFinishAndNavigate = (path: string) => {
    onComplete();
    navigate(path);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="advisor-wizard"
    >
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
              <StepIcon className="h-6 w-6 text-amber-500" />
            </div>
          </div>
          <CardTitle className="text-xl">{step.title}</CardTitle>
          <CardDescription>{step.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {step.content}
          </p>

          {/* Step indicators */}
          <div className="flex justify-center gap-1.5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === currentStep
                    ? 'w-6 bg-amber-500'
                    : i < currentStep
                    ? 'w-1.5 bg-amber-500/50'
                    : 'w-1.5 bg-muted-foreground/20'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-3">
            {currentStep > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setCurrentStep(currentStep - 1)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFinish}
                data-testid="advisor-wizard-skip"
              >
                Skip
              </Button>
            )}

            {isLastStep ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleFinishAndNavigate('/advisor/profile')}>
                  Set Up Profile
                </Button>
                <Button size="sm" onClick={() => handleFinishAndNavigate('/advisor/clients')}>
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Get Started
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={() => setCurrentStep(currentStep + 1)}>
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
